/**
 * The kind of record or business event that caused a ledger entry to be written. Every
 * component, allocation, funding, and disbursement carries one, so an entry is always
 * traceable back to what produced it — and, for automated integrations, so retries can be
 * deduped instead of double-posting.
 */
export enum SourceType {
  /** The rating matrix, evaluated at warranty creation — components only. */
  RATING = 'RATING',
  /** A sale, cancellation, reinstatement, or adjustment on the warranty itself — allocations only. */
  WARRANTY_LIFECYCLE = 'WARRANTY_LIFECYCLE',
  /** An existing @vsolv/packages/payments Payment.Model reaching PAID, or a refund/void recorded on one. */
  PAYMENT = 'PAYMENT',
  /** A BatchPayment.Model grouping several warranties into one seller remittance. */
  BATCH_PAYMENT = 'BATCH_PAYMENT',
  /** Entered directly by a staff user outside of an automated integration, e.g. a manual correction. */
  MANUAL = 'MANUAL',
}

interface RatingSource {
  readonly type: SourceType.RATING;
  /**
   * The matched rating-matrix cell id, for a component built from one specific cell's
   * contribution (see DynamicPricingService.evaluatePlanPricing). Null for a component that
   * isn't attributable to a single cell — a fee computed by the condition-rule engine, a
   * fallback residual/markup/tax line.
   */
  readonly id: string | null;
}

interface WarrantyLifecycleSource {
  readonly type: SourceType.WARRANTY_LIFECYCLE;
  /** id of the record that triggered the change, when it came through one; null when the warranty's own state change is the only reference needed. */
  readonly id: string | null;
}

interface PaymentSource {
  readonly type: SourceType.PAYMENT;
  /** Payment.Model id. */
  readonly id: string;
}

interface BatchPaymentSource {
  readonly type: SourceType.BATCH_PAYMENT;
  /** BatchPayment.Model id. */
  readonly id: string;
}

interface ManualSource {
  readonly type: SourceType.MANUAL;
  /** Always null — a manual entry has no automated record to reference. */
  readonly id: null;
}

/**
 * The specific record that caused a ledger entry to be written. A discriminated union, so each
 * ledger entry type can narrow `source` to only the SourceTypes actually valid for it (see
 * Component/Allocation/Funding/Disbursement) instead of accepting any SourceType with any id.
 *
 * Idempotency policy for event-driven ledger writes (funding, disbursement — VS-1285):
 *
 *  - Key: (warrantyId, source.type, source.id) is the stable idempotency key. PAYMENT and
 *    BATCH_PAYMENT sources always carry a real id (the Payment/BatchPayment's own id, or —
 *    for a reversal/refund — the reversal/refund event's own id, never the original payment's,
 *    so it doesn't collide with the entry it reverses/refunds). Processing the same source event
 *    twice — a retried charge, a saga redelivering a command — reaches the same key and is a
 *    no-op the second time, at two layers: an app-level pre-check (the fast path) and a DB
 *    unique index, UQ_CONTRACT_FUNDING_WARRANTY_SOURCE / UQ_CONTRACT_DISBURSEMENT_WARRANTY_SOURCE
 *    (the actual guarantee — a concurrent duplicate can still win the pre-check's race, and
 *    losing the resulting insert race is treated as the same no-op, not an error; see
 *    isDuplicateKeyError). MANUAL always has a null id and is deliberately exempt from both:
 *    a human correction is never deduped against another one.
 *  - Failure: recording is a plain synchronous write inside the command handler the triggering
 *    event's saga dispatches. A thrown error there is caught, logged (at error level), and
 *    published to CQRS's UnhandledExceptionBus by @nestjs/cqrs's own saga runner — the saga
 *    itself keeps running for later events rather than dying, and the failed write is retryable
 *    because dispatching the same command again reaches the same idempotency key above.
 *  - Out-of-order: a funding/disbursement command re-reads its source record's current state
 *    rather than trusting the event, but checks a fact that can't be un-set by a later event
 *    (e.g. Payment.paymentDate, set once by markAsPaid() and never cleared by a refund) rather
 *    than current status — RecordPaymentFundingCommand and RecordRefundDisbursementCommand are
 *    dispatched from independent sagas with no ordering guarantee between them, so a
 *    later-published refund can finish first and move status past PAID before the funding
 *    command's own check runs. Checking status there instead of paymentDate would silently skip
 *    funding for a payment that really was paid, leaving a refund disbursement with no matching
 *    funding entry — exactly the silent financial loss this policy exists to prevent.
 *  - Tenant boundary: every check above (pre-check, unique index, command dispatch) runs against
 *    one tenant's own connection/schema (MultiTenantConnectionManager) — there is no shared
 *    table a cross-tenant id collision could reach.
 */
export type SourceReference =
  | RatingSource
  | WarrantyLifecycleSource
  | PaymentSource
  | BatchPaymentSource
  | ManualSource;
